import { canvasProjectionSignature, type CanvasLayerKey, type CanvasProjection, type CanvasSceneMode } from '../../lib/canvasProjection'
import type {
  CanvasCameraPose,
  CanvasLayerVisibility,
  CanvasCaptureViewState,
  PresentationCaptureBinding,
  PresentationScope,
} from '../../types'

export const CANVAS_RENDERER_VERSION = 'suivi-aziz-canvas-3d-v1'

type CaptureBindingInput = {
  id: string
  draftId: string
  slideId: string
  scope: PresentationScope
  projection: CanvasProjection
  referenceProjection?: CanvasProjection
  mode: CanvasSceneMode
  camera: CanvasCameraPose
  visibleLayers: CanvasLayerVisibility
  viewState: CanvasCaptureViewState
  consultedPhaseId?: string
  activeLayer?: CanvasLayerKey
  blob: Blob
  width: number
  height: number
  createdAt?: string
}

type PresentationCaptureMatchInput = {
  draftId: string
  slideId: string
  scope: PresentationScope
  projection: CanvasProjection
  referenceProjection?: CanvasProjection
}

export async function hashBlob(blob: Blob) {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer())
  return Array.from(new Uint8Array(digest)).map((value) => value.toString(16).padStart(2, '0')).join('')
}

function renderedReferenceProjection(
  projection: CanvasProjection,
  referenceProjection: CanvasProjection | undefined,
  mode: CanvasSceneMode,
  visibleLayers: CanvasLayerVisibility,
) {
  if (mode !== 'layers-3d' || !visibleLayers.reference) return undefined
  return referenceProjection ?? (projection.scope === 'general' ? projection : undefined)
}

function renderedProjectionSignature(projection: CanvasProjection) {
  const signature = canvasProjectionSignature(projection)
  return {
    ...signature,
    entity: {
      id: signature.entity.id,
      version: signature.entity.version,
      referenceId: signature.entity.referenceId,
      asOfDate: signature.entity.asOfDate,
      fictitious: signature.entity.fictitious,
    },
  }
}

/**
 * A canonical, exact description of the data rendered in a 3D scene.
 * It is deliberately a serialised source signature rather than a lossy short hash:
 * the binding can be checked synchronously just before the presentation is frozen.
 */
export function canvasSceneProjectionSignature(input: {
  projection: CanvasProjection
  referenceProjection?: CanvasProjection
  mode: CanvasSceneMode
  visibleLayers: CanvasLayerVisibility
}) {
  const reference = renderedReferenceProjection(input.projection, input.referenceProjection, input.mode, input.visibleLayers)
  return JSON.stringify({
    rendered: renderedProjectionSignature(input.projection),
    reference: reference ? renderedProjectionSignature(reference) : null,
  })
}

export async function createPresentationCaptureBinding(input: CaptureBindingInput): Promise<PresentationCaptureBinding> {
  if (input.scope !== input.projection.scope) throw new Error('La capture ne correspond pas au contexte de la présentation.')
  const phaseIds = new Set([
    ...input.projection.phases.map((phase) => phase.id),
    ...(input.referenceProjection?.phases.map((phase) => phase.id) ?? []),
  ])
  if (input.viewState === 'focus' && (!input.consultedPhaseId || !phaseIds.has(input.consultedPhaseId))) {
    throw new Error('La phase focalisée de cette capture n’est plus disponible.')
  }
  return {
    id: input.id,
    draft_id: input.draftId,
    slide_id: input.slideId,
    scope: input.scope,
    entity_id: input.projection.entity.id,
    source_version: input.projection.entity.version,
    reference_id: input.projection.entity.referenceId,
    as_of_date: input.projection.entity.asOfDate,
    mode: input.mode,
    camera: structuredClone(input.camera),
    visible_layers: structuredClone(input.visibleLayers),
    view_state: input.viewState,
    consulted_phase_id: input.consultedPhaseId,
    actual_current_phase_id: input.projection.currentPhaseId,
    actual_current_step_id: input.projection.currentStepId,
    active_layer: input.mode === 'layers-3d' ? input.activeLayer : undefined,
    projection_signature: canvasSceneProjectionSignature(input),
    renderer_version: CANVAS_RENDERER_VERSION,
    width: input.width,
    height: input.height,
    png_sha256: await hashBlob(input.blob),
    created_at: input.createdAt ?? new Date().toISOString(),
  }
}

export function captureMatchesPresentation(
  capture: PresentationCaptureBinding,
  input: PresentationCaptureMatchInput,
) {
  const expectedSignature = canvasSceneProjectionSignature({
    projection: input.projection,
    referenceProjection: input.referenceProjection,
    mode: capture.mode,
    visibleLayers: capture.visible_layers,
  })
  return capture.draft_id === input.draftId
    && capture.slide_id === input.slideId
    && capture.scope === input.scope
    && capture.entity_id === input.projection.entity.id
    && capture.source_version === input.projection.entity.version
    && capture.reference_id === input.projection.entity.referenceId
    && capture.as_of_date === input.projection.entity.asOfDate
    && capture.projection_signature === expectedSignature
    && capture.renderer_version === CANVAS_RENDERER_VERSION
}

function safeFilePart(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '')
}

export function standaloneCaptureFilename(projection: CanvasProjection, mode: CanvasSceneMode) {
  const context = safeFilePart(projection.entity.name || projection.entity.code)
  return `${context}-v${projection.entity.version}-${projection.entity.asOfDate}-${mode}.png`
}

export function downloadCapture(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000)
}
